/**
 * DataViewer
 * 
 * Tabular data viewer with sorting and filtering
 */

import React, { useState, useMemo } from 'react';
import type { DataArtifact } from '../../artifacts';
import { useWorkspace } from '../../artifacts';

interface DataViewerProps {
  artifact: DataArtifact;
}

export const DataViewer: React.FC<DataViewerProps> = ({ artifact }) => {
  const { updateArtifact } = useWorkspace();
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filter, setFilter] = useState('');

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedRows = useMemo(() => {
    let rows = [...artifact.rows];

    // Filter
    if (filter) {
      const lowerFilter = filter.toLowerCase();
      rows = rows.filter((row) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(lowerFilter)
        )
      );
    }

    // Sort
    if (sortColumn) {
      rows.sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];
        
        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        const comparison = aVal < bVal ? -1 : 1;
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return rows;
  }, [artifact.rows, filter, sortColumn, sortDirection]);

  const isEmpty = artifact.columns.length === 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-white">
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={artifact.title}
            onChange={(e) => updateArtifact(artifact.id, { title: e.target.value })}
            className="text-lg font-medium text-neutral-900 bg-transparent border-none outline-none focus:ring-0"
            placeholder="Data View Title"
          />
          <span className="text-xs text-neutral-500">
            {artifact.rows.length} rows · {artifact.columns.length} columns
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <svg
              className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              placeholder="Filter data..."
            />
          </div>
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-medium rounded bg-neutral-100 text-neutral-700 hover:bg-neutral-200 transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 mb-4 rounded-xl bg-neutral-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-neutral-700 mb-2">No Data</h3>
            <p className="text-sm text-neutral-500 max-w-sm">
              Upload a file or ask the assistant to populate this data view.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-neutral-50 z-10">
              <tr>
                {artifact.columns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="px-4 py-2.5 text-left font-medium text-neutral-600 border-b border-neutral-200 cursor-pointer hover:bg-neutral-100 transition-colors"
                  >
                    <div className="flex items-center gap-1.5">
                      {col.label}
                      {sortColumn === col.key && (
                        <svg
                          className={`w-3.5 h-3.5 transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedRows.map((row, idx) => (
                <tr key={idx} className="hover:bg-blue-50/50 transition-colors">
                  {artifact.columns.map((col) => (
                    <td key={col.key} className="px-4 py-2.5 text-neutral-800 border-b border-neutral-100">
                      {formatValue(row[col.key], col.type, col.format)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      {!isEmpty && (
        <div className="px-4 py-2 border-t border-neutral-200 bg-neutral-50 text-xs text-neutral-500">
          Showing {filteredAndSortedRows.length} of {artifact.rows.length} rows
          {filter && ` (filtered by "${filter}")`}
        </div>
      )}
    </div>
  );
};

function formatValue(value: unknown, type: string, format?: string): string {
  if (value === null || value === undefined) return '—';
  
  if (type === 'currency') {
    const num = Number(value);
    return isNaN(num) ? String(value) : `$${num.toLocaleString()}`;
  }
  
  if (type === 'date') {
    const date = new Date(String(value));
    return isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
  }
  
  if (type === 'number') {
    const num = Number(value);
    return isNaN(num) ? String(value) : num.toLocaleString();
  }
  
  return String(value);
}

export default DataViewer;

